import { StatsAdapterInterface } from '../../../stats/adapter.interface';
import { StatsContextInterface } from '../../../stats/context.interface';
import { GtmStatsDataEntityInterface } from '../data/entity.interface';
import { GtmStatsDataEventLeadInterface } from '../data/event-lead.interface';
import { GtmStatsAdapterEnquiryType } from './enquiry-type';

export class GtmStatsAdapterEventLead implements StatsAdapterInterface {
  public static LABEL_DESCRIPTION_EXPANDED: string = 'Description Expanded';
  public static LABEL_DESCRIPTION_COLLAPSED: string = 'Description Collapsed';
  public static LABEL_CALL: string = 'Call Now';
  public static LABEL_CALL_BUTTON: string = 'Button';
  public static LABEL_CALL_LINK: string = 'Text';
  public static LABEL_PROPERTY_PAGE: string = 'Property Detail';
  public static LABEL_PROPERTY_SERP: string = 'Property Serp';
  public static LABEL_PROPERTY_SAVED: string = 'Saved Properties';
  public static LABEL_PROPERTY_CONTACTED: string = 'Contacted Properties';
  public static LABEL_PROPERTY_MY_NOTES: string = 'My Notes Properties';
  public static LABEL_AGENT_PAGE: string = 'Agent Detail';
  public static LABEL_AGENT_SERP: string = 'Agent Serp';
  public static LABEL_AGENT_HOME: string = 'Agent Home';
  public static LABEL_BROKER_PAGE: string = 'Broker Detail';
  public static LABEL_BROKER_SERP: string = 'Broker Serp';
  public static LABEL_MAP_SEARCH: string = 'Map Search';
  public static LABEL_BROKER_HOME: string = 'Broker Home';

  /**
   *
   * @param entity
   * @param context
   */
  constructor(protected entity: GtmStatsDataEntityInterface, protected context: StatsContextInterface) {}

  /**
   * @inheritDoc
   */
  public getData(): GtmStatsDataEventLeadInterface {
    const data: GtmStatsDataEventLeadInterface = {
      event: this.entity.name,
      eventAction: 'Email',
      eventLabel: '',
    };

    const label: string[] = [];

    if (!this.context.local.lead) {
      throw new Error('Stats local context "lead" is not set.');
    }

    const contextLabel = this.getContextLabel();
    if (contextLabel) {
      label.push(contextLabel);
    }

    // Depending on the medium
    switch (this.context.local.lead.medium) {
      case 'whatsapp':
        data.eventAction = 'Whatsapp';
        // Variation
        switch (this.context.local.lead.variation) {
          case 'live-viewing-popup':
            label.push('Live Viewing Popup');
            break;
        }

        break;

      case 'call_back':
        data.eventAction = 'CallBack';

        break;

      case 'phone':
        data.eventAction = 'Call';

        // Entity page
        if (
          this.context.global.property_page ||
          this.context.global.agent_page ||
          this.context.global.broker_page ||
          this.context.global.my_account
        ) {
          switch (this.context.local.lead.cta) {
            // Phone link
            case 'link':
              if (this.context.local.entity_description_expanded !== undefined) {
                label.push(
                  this.context.local.entity_description_expanded
                    ? GtmStatsAdapterEventLead.LABEL_DESCRIPTION_EXPANDED
                    : GtmStatsAdapterEventLead.LABEL_DESCRIPTION_COLLAPSED
                );
              } else {
                switch (this.context.local.lead.variation) {
                  case 'content-reveal':
                    label.push(GtmStatsAdapterEventLead.LABEL_CALL_LINK);
                    break;
                }
              }

              break;

            // Phone button
            case 'button':
              switch (this.context.local.lead.variation) {
                case 'content-reveal':
                  label.push(GtmStatsAdapterEventLead.LABEL_CALL_BUTTON);
                  break;

                default:
                  label.push(GtmStatsAdapterEventLead.LABEL_CALL);
              }

              break;
          }
        }
        break;

      case 'sms':
        data.eventAction = 'SMS';

        // Variation
        switch (this.context.local.lead.variation) {
          case 'live-viewing-popup':
            label.push('Live Viewing Popup');
            break;
        }
        break;

      case 'email':
        data.eventAction = 'Email';

        // Enquiry type
        if (this.context.local.lead.email.enquiryType) {
          data.enquiry = new GtmStatsAdapterEnquiryType(this.context.local.lead.email.enquiryType).getData();
        }

        // Variation
        switch (this.context.local.lead.variation) {
          case 'viewing':
            label.push('Book a Viewing');
            break;

          default:
            // Entity page
            if (
              this.context.global.property_page ||
              this.context.global.agent_page ||
              this.context.global.broker_page ||
              this.context.global.my_account
            ) {
              label.push(data.eventAction);
            }
        }

        break;
    }

    data.eventLabel = label.join(' - ');

    return data;
  }

  /**
   * Returns the label linked to the context
   */
  public getContextLabel(): string {
    // Property page
    if (this.context.global.property_page) {
      return GtmStatsAdapterEventLead.LABEL_PROPERTY_PAGE;
    }

    // Agent page
    if (this.context.global.agent_page) {
      return GtmStatsAdapterEventLead.LABEL_AGENT_PAGE;
    }

    // Broker page
    if (this.context.global.broker_page) {
      return GtmStatsAdapterEventLead.LABEL_BROKER_PAGE;
    }

    if (this.context.global.map_search_page) {
      return GtmStatsAdapterEventLead.LABEL_MAP_SEARCH;
    }

    // Property SERP
    if (this.context.global.property_serp) {
      return GtmStatsAdapterEventLead.LABEL_PROPERTY_SERP;
    }

    // Agent SERP
    if (this.context.global.agent_serp) {
      return GtmStatsAdapterEventLead.LABEL_AGENT_SERP;
    }

    // Broker SERP
    if (this.context.global.broker_serp) {
      return GtmStatsAdapterEventLead.LABEL_BROKER_SERP;
    }

    // Agent home
    if (this.context.global.agent_home) {
      return GtmStatsAdapterEventLead.LABEL_AGENT_HOME;
    }

    // Broker home
    if (this.context.global.broker_home) {
      return GtmStatsAdapterEventLead.LABEL_BROKER_HOME;
    }

    // Property save
    if (this.context.global.user_page) {
      return GtmStatsAdapterEventLead.LABEL_PROPERTY_SAVED;
    }

    // Saved properties
    if (this.context.global.saved_properties) {
      return GtmStatsAdapterEventLead.LABEL_PROPERTY_SAVED;
    }

    // Contacted properties
    if (this.context.global.contacted_properties) {
      return GtmStatsAdapterEventLead.LABEL_PROPERTY_CONTACTED;
    }

    // Contacted properties
    if (this.context.global.my_notes_properties) {
      return GtmStatsAdapterEventLead.LABEL_PROPERTY_MY_NOTES;
    }

    return '';
  }
}
